import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext.jsx";
import { getUserTickets } from "../../services/ticketService";
import RaiseTicketModal from "../../components/Support/RaiseTicketModal.jsx";
import TicketDetailModal from "../../components/Support/TicketDetailView.jsx";
import { Plus, Ticket as TicketIcon, Clock, AlertCircle, CheckCircle, XCircle } from "lucide-react";

const SupportTickets = () => {
  const { user } = useAuth();
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [userTickets, setUserTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  // Load user tickets
  useEffect(() => {
    if (user?.id) {
      loadUserTickets();
    }
  }, [user]);

  const loadUserTickets = async () => {
    if (!user?.id) return;
    setLoadingTickets(true);
    try {
      const tickets = await getUserTickets(user.id);
      setUserTickets(tickets);
    } catch (error) {
      console.error("Error loading tickets:", error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <AlertCircle className="w-4 h-4 text-blue-600" />;
      case "in_progress":
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case "resolved":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "closed":
        return <XCircle className="w-4 h-4 text-gray-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Minimal Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-gradient-to-br from-red-600 to-red-700 rounded-lg shadow-md">
              <TicketIcon className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Support Tickets</h1>
          </div>
          <p className="text-gray-600 text-sm max-w-xl">Track and manage your support requests with real-time progress updates.</p>
        </div>

        {/* Action Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowTicketModal(true)}
            className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-red-700 hover:to-red-800 transition-all duration-300 transform hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4" />
            <span>Create Ticket</span>
          </button>
        </div>

        {/* Tickets Container */}
        {loadingTickets ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative w-10 h-10 mb-3">
              <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 rounded-full animate-spin"></div>
              <div className="absolute inset-1 bg-white rounded-full"></div>
            </div>
            <p className="text-gray-600 text-sm font-medium">Loading tickets...</p>
          </div>
        ) : userTickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mb-3">
              <TicketIcon className="w-7 h-7 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Support Tickets</h3>
            <p className="text-gray-600 text-sm text-center mb-4 max-w-sm">Create your first support ticket to get assistance from our team.</p>
            <button
              onClick={() => setShowTicketModal(true)}
              className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              Create Ticket
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {userTickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicketId(ticket.id)}
                className="group bg-white rounded-lg border border-gray-200 hover:border-red-400 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden hover:translate-y-[-2px]"
              >
                <div className="relative p-3.5 sm:p-4">
                  {/* Animated gradient border on hover */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {/* Left Section */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2.5 py-1 bg-gradient-to-r from-red-50 to-red-100 text-red-700 text-xs font-bold rounded-md font-mono border border-red-200">
                          {ticket.ticketCode}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(ticket.status)}
                          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold transition-colors duration-300 ${getStatusColor(ticket.status)}`}>
                            {ticket.status.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 group-hover:text-red-600 transition-colors duration-300 truncate">
                        {ticket.subject}
                      </h3>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                        {ticket.description}
                      </p>
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center justify-between sm:flex-col sm:text-right flex-shrink-0 text-xs">
                      <p className="text-gray-500 font-medium hidden sm:block">
                        {formatDate(ticket.updatedAt || ticket.createdAt).split(",")[0]}
                      </p>
                      <div className="text-red-600 group-hover:translate-x-0.5 transition-transform duration-300 sm:mt-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Raise Ticket Modal */}
      {user && (
        <RaiseTicketModal
          isOpen={showTicketModal}
          onClose={() => {
            setShowTicketModal(false);
            loadUserTickets();
          }}
          userType={user.userType || "jobseeker"}
        />
      )}

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        ticketId={selectedTicketId}
        isOpen={!!selectedTicketId}
        onClose={() => {
          setSelectedTicketId(null);
          loadUserTickets();
        }}
      />
    </div>
  );
};

export default SupportTickets;

